import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { IoCloseOutline } from 'react-icons/io5';
import { FiPlus } from 'react-icons/fi';
import { LuEye, LuEyeOff, LuImage } from 'react-icons/lu';
import { toast } from 'react-toastify';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import methodModel from '../../methods/methods';
import Layout from '../../components/global/layout';
import FormControl from '../../components/common/FormControl';
import shared from './shared';
import permissionModel from '../../models/permisstion.model';
import { CgArrowLeft } from 'react-icons/cg';
import { FaChevronLeft } from 'react-icons/fa';
import { RiFileList3Line } from 'react-icons/ri';
import { FaListCheck } from 'react-icons/fa6';

// ─── Password strength rules ───────────────────────────────────────────────
const PASSWORD_RULES = [
  { key: 'minLength', label: 'At least 8 characters',        test: (v) => v.length >= 8 },
  { key: 'uppercase', label: 'At least 1 uppercase letter',  test: (v) => /[A-Z]/.test(v) },
  { key: 'lowercase', label: 'At least 1 lowercase letter',  test: (v) => /[a-z]/.test(v) },
  { key: 'number',    label: 'At least 1 number',            test: (v) => /[0-9]/.test(v) },
  { key: 'special',   label: 'At least 1 special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const validatePassword = (v) => PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(v) }));
const isPasswordValid  = (v) => PASSWORD_RULES.every((r) => r.test(v));

function AddEditStaff() {
  const { id } = useParams();
  const [form, setform] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNo: '',
    password: '',
    confirmPassword: '',
    role: shared?.role,
    permissions: [...permissionModel.permissions],
  });
  const history = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [eyes, setEyes] = useState({ password: false, confirmPassword: false });

  const inValidEmail = methodModel.emailvalidation(form?.email);

  // ── Password derived values ──
  const passwordFilled = form.password.length > 0;
  const passwordRules  = validatePassword(form.password);
  const passwordValid  = isPasswordValid(form.password);
  const confirmMatch   = form.confirmPassword === form.password;
  const passedCount    = passwordRules.filter((r) => r.passed).length;

  const strengthLabel =
    passedCount <= 1 ? 'Weak' : passedCount <= 3 ? 'Fair' : passedCount === 4 ? 'Good' : 'Strong';
  const strengthColor =
    passedCount <= 1 ? '#ef4444' : passedCount <= 3 ? '#f59e0b' : passedCount === 4 ? '#3b82f6' : '#22c55e';

  const formValidation = [
    { key: 'firstName', required: true },
    { key: 'lastName', required: true },
    { key: 'email', required: true, message: 'Email is required', email: true },
  ];

  useEffect(() => {
    if (id) {
      loader(true);
      ApiClient.get(shared.detailApi, { id }).then((res) => {
        if (res.success) {
          const existingPermissions = res?.data?.permissions || [];
          const mergedPermissions = permissionModel.permissions.map((perm) => {
            const existingPerm = existingPermissions.find((p) => p.module === perm.module);
            return existingPerm ? { ...perm, ...existingPerm } : { ...perm };
          });
          setform({
            firstName: res?.data?.firstName || '',
            lastName: res?.data?.lastName || '',
            email: res?.data?.email || '',
            mobileNo: res?.data?.mobileNo || '',
            image: res?.data?.image || '',
            role: res?.data?.role,
            id: res?.data?.id || res?.data?._id,
            password: '',
            confirmPassword: '',
            permissions: mergedPermissions || permissionModel.permissions,
          });
        }
        loader(false);
      });
    } else {
      setform({
        firstName: '',
        lastName: '',
        email: '',
        mobileNo: '',
        password: '',
        confirmPassword: '',
        role: shared?.role,
        permissions: permissionModel.permissions.map((item) => {
          return { ...item, add: false, edit: false, read: false, delete: false };
        }),
      });
    }
  }, [id]);

  const handlePermissionChange = (index, name, checked) => {
    if (name == 'read' && !checked) {
      const updatedPermissions = [...form.permissions];
      updatedPermissions[index][name] = checked;
      updatedPermissions[index]['edit'] = checked;
      updatedPermissions[index]['delete'] = checked;
      setform((prev) => ({ ...prev, permissions: updatedPermissions }));
    } else {
      const updatedPermissions = [...form.permissions];
      updatedPermissions[index][name] = checked;
      setform((prev) => ({ ...prev, permissions: updatedPermissions }));
    }
  };

  const handleMasterCheckbox = (index, checked) => {
    const updatedPermissions = [...form.permissions];
    updatedPermissions[index].add = checked;
    updatedPermissions[index].edit = checked;
    updatedPermissions[index].delete = checked;
    updatedPermissions[index].read = checked;
    setform((prev) => ({ ...prev, permissions: updatedPermissions }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    const invalid = methodModel.getFormError(formValidation, form);
    if (invalid) return;

    const isAddMode = !id;
    if (isAddMode) {
      if (!passwordValid) return;
      if (!confirmMatch) return;
    } else {
      if (passwordFilled) {
        if (!passwordValid) return;
        if (!confirmMatch) return;
      }
    }

    let method = 'post';
    let url = shared.addApi;
    const value = { ...form };
    delete value?.userId;
    delete value.confirmPassword;

    if (id) {
      method = 'put';
      url = shared.editApi;
      if (!passwordFilled) {
        delete value.password;
      }
    } else {
      delete value.id;
    }

    delete value['userId'];
    value['role'] = value?.role?.id || value?.role?._id || value?.role;

    loader(true);
    ApiClient.allApi(url, value, method).then((res) => {
      if (res.success) {
        history(`/${shared.route}`);
      }
      loader(false);
    });
  };

  const ImageUpload = (e) => {
    const { files } = e.target;
    const file = files.item(0);
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG and PNG images are allowed.');
      return;
    }
    loader(true);
    ApiClient.postFormData('upload/image?model=user', { file }).then((res) => {
      if (res.success) {
        setform({ ...form, image: res?.data?.filePath });
      }
      loader(false);
    });
  };

  const SelectAll = (key = 'all', checked) => {
    let permissions = form?.permissions;
    let UpdatePermission = [];
    if (key == 'all') {
      permissions.map((item) => {
        UpdatePermission.push({ ...item, edit: checked, read: checked, delete: checked });
      });
    } else {
      if (key == 'read' && !checked) {
        permissions.map((item) => {
          UpdatePermission.push({ ...item, read: checked, edit: checked, delete: checked });
        });
      } else {
        permissions.map((item) => {
          UpdatePermission.push({ ...item, [key]: checked });
        });
      }
    }
    setform({ ...form, permissions: UpdatePermission });
  };

  const MasterFinder = (key = '') => {
    let value = false;
    form?.permissions?.map((item) => {
      if (!item?.read && !item?.hide?.includes(key)) {
        value = true;
      }
    });
    return value;
  };

  const MasterCheckboxFinder = (key = 'all') => {
    let value = true;
    const permissionList = form?.permissions;
    if (key == 'all') {
      permissionList.map((item) => {
        Object.keys(item).map((ObjectKey) => {
          if (item?.hide?.includes(ObjectKey) || ObjectKey == 'hide' || ObjectKey == 'module' || ObjectKey == 'add') {
            return;
          } else {
            if (!item[ObjectKey]) {
              value = false;
            }
          }
        });
      });
    } else {
      permissionList.map((item) => {
        if (!item?.[key] && !item?.hide?.includes(key)) {
          value = false;
        }
      });
    }
    return value;
  };

  const ModuleCheckbox = (module) => {
    const keys = ['read', 'edit', 'delete'];
    let filtersKeys = keys.filter((item) => !module?.hide?.includes(item));
    if (filtersKeys.length > 2) {
      return module[filtersKeys[0]] && module[filtersKeys[1]] && module[filtersKeys[2]];
    } else {
      if (filtersKeys.length == 1) {
        return module[filtersKeys[0]];
      } else {
        return module[filtersKeys[0]] && module[filtersKeys[1]];
      }
    }
  };

  return (
    <Layout>
      <form onSubmit={handleSubmit}>
        <div className="pprofile1">
          <div className="flex gap-3 mb-8">
            <Tooltip placement="top" title="Back">
              <Link to={`/${shared.route}`} className="min-w-[30px] w-[30px] h-[30px] rounded-full bg-[#CC9900] text-white flex items-center justify-center hover:opacity-80">
                <FaChevronLeft />
              </Link>
            </Tooltip>
            <div>
              <h3 className="text-lg lg:text-2xl font-semibold text-[#221E10]">
                {form && form.id ? 'Edit' : 'Add'} {shared.addTitle}
              </h3>
              <p className='text-sm text-[#2D2D2D]'>  Fill in the details below to add a new staff member</p>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-6'>

         <div className='bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border'>
            <div className='mb-5'>
                          <div className='flex gap-2'>
                          <RiFileList3Line className="text-[#CC9900] text-xl relative top-[1px]" />
                         <h4 className='text-[#2D2D2D] font-semibold'>Basic Information</h4>
                          </div>
                          <p className='text-sm text-[#717182]'>  Enter the essential details about the staff member</p>
                    </div>
          <div className="grid grid-cols-12 gap-5">

            {/* ── First Name ── */}
            <div className="col-span-12 sm:col-span-6">
              <FormControl
                type="name"
                name="firstName"
                label="First Name"
                placeholder="Enter First Name"
                value={form?.firstName}
                onChange={(e) => setform({ ...form, firstName: e })}
                required
              />
              {submitted && !form.firstName && (
                <div className="d-block text-red-600">First Name is required</div>
              )}
            </div>

            {/* ── Last Name ── */}
            <div className="col-span-12 sm:col-span-6">
              <FormControl
                type="name"
                name="lastName"
                label="Last Name"
                placeholder="Enter Last Name"
                value={form?.lastName}
                onChange={(e) => setform({ ...form, lastName: e })}
                required
              />
              {submitted && !form.lastName && (
                <div className="d-block text-red-600">Last Name is required</div>
              )}
            </div>

            {/* ── Email ── */}
            <div className="col-span-12 sm:col-span-6">
              <FormControl
                type="text"
                name="email"
                label="Email"
                placeholder="Enter Email"
                className={id ? 'cursor-not-allowed' : ''}
                value={form.email}
                onChange={(e) => setform({ ...form, email: e })}
                required
                disabled={!!id}
              />
              {form?.email && submitted && !inValidEmail && (
                <div className="d-block text-red-600">Please enter valid email</div>
              )}
            </div>

            {/* ── Mobile Number ── */}
            <div className="col-span-12 sm:col-span-6 mobile_number">
              <FormControl
                type="phone"
                name="mobileNo"
                label="Mobile Number"
                value={form.mobileNo}
                onChange={(e) => setform({ ...form, mobileNo: e })}
              />
            </div>

            {/* ── Password ── */}
            <div className="col-span-12 sm:col-span-6">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Password{!id && <span className="text-red-500"> *</span>}
                {id && <span className="text-gray-400 text-xs font-normal ml-1">(leave blank to keep unchanged)</span>}
              </label>
              <div className="relative">
                <input
                  type={eyes.password ? 'text' : 'password'}
                  className="relative  bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed pr-10 "
                  value={form.password}
                  maxLength={16}
                  placeholder={id ? 'Enter new password (optional)' : 'Enter password'}
                  onChange={(e) =>
                    setform({
                      ...form,
                      password: e.target.value,
                      confirmPassword: e.target.value === '' ? '' : form.confirmPassword,
                    })
                  }
                  required={!id}
                />
                <div className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer">
                  <i onClick={() => setEyes({ ...eyes, password: !eyes.password })}>
                    {eyes.password
                      ? <LuEye className="text-gray-400" />
                      : <LuEyeOff className="text-gray-400" />}
                  </i>
                </div>
              </div>

              {/* Strength bar + rules — fixed height to prevent layout shift */}
              <div style={{ paddingTop: '8px', overflow: 'hidden' }}>
                <div style={{ opacity: 1, transition: 'opacity 0.2s' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <div
                        key={n}
                        style={{
                          flex: 1,
                          height: '4px',
                          borderRadius: '9999px',
                          background: n <= passedCount ? strengthColor : '#e5e7eb',
                          transition: 'background 0.25s',
                        }}
                      />
                    ))}
                  </div>
                  <p style={{ fontSize: '11px', fontWeight: 600, color: strengthColor, margin: '0 0 6px 0', transition: 'color 0.2s' }}>
                    {strengthLabel}
                  </p>
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {passwordRules.map((rule) => (
                    <li
                      key={rule.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        lineHeight: '1.6',
                        color: !passwordFilled ? '#9ca3af' : rule.passed ? '#22c55e' : '#ef4444',
                        transition: 'color 0.2s',
                      }}
                    >
                      <span style={{ fontSize: '10px', width: '10px', flexShrink: 0 }}>
                        {!passwordFilled ? '·' : rule.passed ? '✓' : '✗'}
                      </span>
                      {rule.label}
                    </li>
                  ))}
                </ul>
              </div>

              {submitted && passwordFilled && !passwordValid && (
                <p style={{ fontSize: '12px', color: '#ef4444', margin: '2px 0 0' }}>
                  Password does not meet all requirements.
                </p>
              )}
              {submitted && !id && !passwordFilled && (
                <p style={{ fontSize: '12px', color: '#ef4444', margin: '2px 0 0' }}>
                  Password is required.
                </p>
              )}
            </div>

            {/* ── Confirm Password ── */}
            <div className="col-span-12 sm:col-span-6">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Confirm Password{!id && <span className="text-red-500"> *</span>}
              </label>
              <div className="relative">
                <input
                  type={eyes.confirmPassword ? 'text' : 'password'}
                  className={`relative  bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed pr-10 ${id && !passwordFilled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  maxLength={16}
                  disabled={id && !passwordFilled}
                  onChange={(e) => setform({ ...form, confirmPassword: e.target.value })}
                  required={!id || passwordFilled}
                />
                <div className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer">
                  <i onClick={() => { if (!id || passwordFilled) setEyes({ ...eyes, confirmPassword: !eyes.confirmPassword }); }}>
                    {eyes.confirmPassword
                      ? <LuEye className="text-gray-400" />
                      : <LuEyeOff className="text-gray-400" />}
                  </i>
                </div>
              </div>

              {/* Fixed-height confirm feedback */}
              <div style={{ height: '20px', marginTop: '4px' }}>
                {passwordFilled && form.confirmPassword.length > 0 ? (
                  <p style={{ fontSize: '12px', color: confirmMatch ? '#22c55e' : '#ef4444', transition: 'color 0.2s', margin: 0 }}>
                    {confirmMatch ? '✓ Passwords match' : '✗ Passwords does not match'}
                  </p>
                ) : submitted && passwordFilled && !form.confirmPassword ? (
                  <p style={{ fontSize: '12px', color: '#ef4444', margin: 0 }}>
                    Please confirm your password.
                  </p>
                ) : null}
              </div>
            </div>

          </div>
          </div>

         <div className='bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border'>
            <div className='mb-5'>
                          <div className='flex gap-2'>
                          <LuImage className="text-[#CC9900] text-xl relative top-[1px]" />
                         <h4 className='text-[#2D2D2D] font-semibold'>Image</h4>
                          </div>
                          <p className='text-sm text-[#717182]'></p>
                    </div>
                    <div className='grid grid-cols-12 gap-5'>
                      <div className='col-span-12 sm:col-span-6'>
            <label className='mb-2 block'>Image (JPG/PNG)</label>
            <div className="flex flex-col rounded-lg cursor-pointer gap-6 max-sm:mx-auto">
              {form?.image ? (
                <div className="flex flex-wrap gap-3">
                  <div className="relative">
                    <img src={methodModel.userImg(form?.image)} className="bg-white thumbnail !w-[100px] !h-[100px] rounded-lg shadow-lg border-[2px] border-white object-contain" />
                    <IoCloseOutline className="absolute -top-2 -right-2 pointer hover:text-red-600 w-5 h-5 border bg-white shadow-md rounded-[50%]" onClick={(e) => setform({ ...form, image: '' })} size={25} />
                  </div>
                </div>
              ) : (
                <label className="flex items-center gap-2 border border-primary border-dashed justify-center cursor-pointer text-black-800 bg-[#fff] focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5">
                  <input type="file" name="image" className="hidden" onChange={ImageUpload} />
                  <FiPlus /> Upload Image
                </label>
              )}
            </div>
            </div>
            </div>
          </div>

          {/* ── Permission Table ── */}
            <div className='bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border'>
            <div className='mb-5'>
                          <div className='flex gap-2'>
                          <FaListCheck className="text-[#CC9900] text-xl relative top-[1px]" />
                         <h4 className='text-[#2D2D2D] font-semibold'>Permissions</h4>
                          </div>
                          <p className='text-sm text-[#717182]'></p>
                    </div>

              <div className="table-design relative table-responsive overflow-x-auto">
                <table className="table w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                  <thead>
                    <tr>
                      <th className="bg-[#CC9900] text-white px-2 py-3">Module</th>
                      <th className="bg-[#CC9900] text-white px-2 py-3">Select All</th>
                      <th className="bg-[#CC9900] text-white px-2 py-3">Read</th>
                      <th className="bg-[#CC9900] text-white px-2 py-3">Add/Edit</th>
                      <th className="bg-[#CC9900] text-white px-2 py-3">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-2 py-4 whitespace-nowrap"></td>
                      <td className="px-2 py-4 whitespace-nowrap">
                        <input type="checkbox" id="master-all" onChange={(e) => SelectAll('all', e.target.checked)} checked={MasterCheckboxFinder('all')} />
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap">
                        <input type="checkbox" id="read-master-all" onChange={(e) => SelectAll('read', e.target.checked)} checked={MasterCheckboxFinder('read')} />
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap">
                        <input type="checkbox" id="edit-master-all" disabled={MasterFinder('edit')} onChange={(e) => SelectAll('edit', e.target.checked)} checked={MasterCheckboxFinder('edit')} />
                      </td>
                      <td className="px-2 py-4 whitespace-nowrap">
                        <input type="checkbox" id="delete-master-all" disabled={MasterFinder('delete')} onChange={(e) => SelectAll('delete', e.target.checked)} checked={MasterCheckboxFinder('delete')} />
                      </td>
                    </tr>
                    {form.permissions.map((module, index) => (
                      <tr key={index} className="">
                        <td className="px-2 py-4 whitespace-nowrap">{module.module}</td>
                        <td className="px-2 py-4 whitespace-nowrap">
                          <input type="checkbox" id={`master-${index}`} onChange={(e) => handleMasterCheckbox(index, e.target.checked)} checked={ModuleCheckbox(module)} aria-label={`Select all permissions for ${module.module}`} />
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap">
                          <input type="checkbox" id={`read-${index}`} checked={module.read} onChange={(e) => handlePermissionChange(index, 'read', e.target.checked)} aria-label={`Read permission for ${module.module}`} />
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap">
                          {module?.hide?.includes('edit') ? null : (
                            <input type="checkbox" id={`edit-${index}`} disabled={!module.read} checked={module.edit} onChange={(e) => handlePermissionChange(index, 'edit', e.target.checked)} aria-label={`Edit permission for ${module.module}`} />
                          )}
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap">
                          {module?.hide?.includes('delete') ? null : (
                            <input type="checkbox" id={`delete-${index}`} checked={module.delete} disabled={!module.read} onChange={(e) => handlePermissionChange(index, 'delete', e.target.checked)} aria-label={`Delete permission for ${module.module}`} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          </div>

          </div>

          {/* ── Submit ── */}
        <div className="flex gap-2 justify-end mt-6">
            <button type="submit" className="bg-primary leading-10 h-10 flex justify-center  items-center shadow-btn px-6 hover:opacity-80 text-sm text-white rounded-full gap-1 cursor-pointer min-w-[94px]">
              {form && form?.id ? 'Update' : 'Save'}
            </button>
          </div>

        </div>
      </form>
    </Layout>
  );
}

export default AddEditStaff;